<script setup>
import { marked } from 'marked'
import { computed } from 'vue'

import { Profile, render } from '@/utils/sanitize'

// Plugins only have to declare the props they actually use. The host
// also passes `file` (normalized) and `parsed` (matches() return value)
// — set inheritAttrs: false so they don't fall through onto the root.
defineOptions({ inheritAttrs: false })

const props = defineProps({
  content: { type: [String, Object], default: '' },
})

const markdownSource = computed(() => {
  const c = props.content
  if (typeof c === 'string') return c
  if (c && typeof c === 'object' && typeof c.content === 'string') {
    return c.content
  }
  return ''
})

// Rendered markdown is genuine HTML, and workflow file content is
// untrusted input — run it through the RICH allowlist before v-html.
const rendered = computed(() =>
  render(marked(markdownSource.value), Profile.RICH),
)
</script>

<template>
  <!-- `rendered` is sanitized with Profile.RICH above. -->
  <!-- eslint-disable-next-line vue/no-v-html -->
  <div class="markdown-preview" v-html="rendered"></div>
</template>

<style lang="scss" scoped>
.markdown-preview {
  padding: 1rem;
  line-height: 1.5;
  font-size: 0.95rem;
  :deep(h1) {
    font-size: 2em;
    margin-bottom: 0.5em;
    font-weight: bold;
  }
  :deep(h2) {
    font-size: 1.5em;
    margin-bottom: 0.5em;
    font-weight: bold;
  }
  :deep(h3) {
    font-size: 1.17em;
    margin-bottom: 0.5em;
    font-weight: bold;
  }
  :deep(p) {
    margin-bottom: 1em;
  }
  :deep(ul),
  :deep(ol) {
    margin-left: 2em;
    margin-bottom: 1em;
  }
  :deep(code) {
    background-color: rgba(0, 0, 0, 0.05);
    padding: 0.2em 0.4em;
    border-radius: 3px;
  }
  :deep(pre) {
    background: #f5f5f5;
    padding: 1rem;
    overflow-x: auto;
    border-radius: 4px;
    margin-bottom: 1em;
  }
  :deep(pre code) {
    display: block;
    padding: 0;
    background: transparent;
  }
  :deep(blockquote) {
    border-left: 3px solid #dbdbdb;
    padding-left: 1rem;
    color: #555;
    margin-bottom: 1em;
  }
}
</style>
